import QueryString from 'query-string';
import { toastr } from 'react-redux-toastr';
import { push } from 'react-router-redux';
import {
  FLAG_GETTING_PAYMENT_METHODS,
  SHOW_ERROR_GETTING_PAYMENT_METHODS,
  CLEAR_ERROR_GETTING_PAYMENT_METHODS,
  GET_PAYMENT_METHODS,
  CLEAR_PAYMENT_METHODS,
  FLAG_GETTING_PAYMENT_METHOD,
  GET_PAYMENT_METHOD,
  CLEAR_PAYMENT_METHOD,
} from '../types';
import { DEFAULT_QUERY_GET_ALL } from '../../config/queries';
import {
  PAYMENT_METHOD_ENDPOINT,
  PAYMENT_METHOD_SECURITY_PROFILE_ENDPOINT,
} from '../../config/endpoints';
import {
  DEFAULT_DELETE_CONFIG,
  DEFAULT_GET_CONFIG,
  DEFAULT_POST_CONFIG,
  DEFAULT_PUT_CONFIG,
} from '../../config/rest';
import { isErrorResponse } from '../../utils/error-handlers';
import { PAYMENT_METHOD_PATH } from '../../config/paths';

const flagGettingPaymentMethods = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_PAYMENT_METHODS,
    payload: flag,
  });

const showErrorGettingPaymentMethods = (error) => (dispatch) =>
  dispatch({
    type: SHOW_ERROR_GETTING_PAYMENT_METHODS,
    payload: error,
  });

const clearErrorGettingPaymentMethods = () => (dispatch) =>
  dispatch({
    type: CLEAR_ERROR_GETTING_PAYMENT_METHODS,
  });

const getPaymentMethods = async (tableFilters) => async (dispatch) => {
  try {
    dispatch(clearErrorGettingPaymentMethods());
    dispatch(flagGettingPaymentMethods(true));

    const query = {
      ...tableFilters,
      size: 100,
    };

    const url = `${PAYMENT_METHOD_ENDPOINT}?${QueryString.stringify(query)}`;
    const response = await fetch(url, DEFAULT_GET_CONFIG);

    await isErrorResponse(response, null, dispatch);

    const paymentMethods = await response.json();

    dispatch({
      type: GET_PAYMENT_METHODS,
      payload: paymentMethods,
    });
  } catch (error) {
    toastr.error('Error', error.message);
    dispatch(showErrorGettingPaymentMethods(error));
  } finally {
    dispatch(flagGettingPaymentMethods(false));
  }
};

const getPaymentMethodsPerSecurityProfile =
  async (query = null) =>
  async (dispatch) => {
    dispatch(clearErrorGettingPaymentMethods());
    dispatch(flagGettingPaymentMethods(true));

    try {
      const parameters = {
        ...DEFAULT_QUERY_GET_ALL,
        query,
      };
      const url = `${PAYMENT_METHOD_SECURITY_PROFILE_ENDPOINT}?${QueryString.stringify(
        parameters,
      )}`;
      const response = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(response, null, dispatch);
      const paymentMethods = await response.json();
      dispatch({
        type: GET_PAYMENT_METHODS,
        payload: paymentMethods,
      });
    } catch ({ message }) {
      dispatch(flagGettingPaymentMethods(false));
      toastr.error('Error', message);
    } finally {
      dispatch(flagGettingPaymentMethods(false));
    }
  };

const clearPaymentMethods = () => (dispatch) =>
  dispatch({
    type: CLEAR_PAYMENT_METHODS,
  });

const flagGettingPaymentMethod = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_PAYMENT_METHOD,
    payload: flag,
  });

const getPaymentMethod =
  async ({ paymentMethodId }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingPaymentMethod(true));
      const url = `${PAYMENT_METHOD_ENDPOINT}/${paymentMethodId}`;
      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });
      await isErrorResponse(response, null, dispatch);
      const detraction = await response.json();
      dispatch({
        type: GET_PAYMENT_METHOD,
        payload: detraction,
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagGettingPaymentMethod(false));
    }
  };

const clearPaymentMethod = () => (dispatch) =>
  dispatch({
    type: CLEAR_PAYMENT_METHOD,
  });

const postPaymentMethod =
  async ({ name, description, transactionCode }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingPaymentMethod(true));
      const payload = {
        name,
        description,
        transactionCode,
      };
      const url = PAYMENT_METHOD_ENDPOINT;
      const response = await fetch(url, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify(payload),
      });
      await isErrorResponse(response, null, dispatch);
      const detraction = await response.json();
      dispatch(push(`${PAYMENT_METHOD_PATH}/${detraction.id}`));
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagGettingPaymentMethod(false));
    }
  };

const putPaymentMethod =
  async (paymentMethodId, { name, description, transactionCode }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingPaymentMethod(true));
      const payload = {
        id: paymentMethodId,
        name,
        description,
        transactionCode,
      };
      const url = `${PAYMENT_METHOD_ENDPOINT}/${paymentMethodId}`;
      const response = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });
      await isErrorResponse(response, null, dispatch);
      const detraction = await response.json();
      dispatch(push(`${PAYMENT_METHOD_PATH}/${detraction.id}`));
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagGettingPaymentMethod(false));
    }
  };

const deletePaymentMethod =
  async ({ paymentMethodId }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingPaymentMethod(true));
      const url = `${PAYMENT_METHOD_ENDPOINT}/${paymentMethodId}`;
      const response = await fetch(url, {
        ...DEFAULT_DELETE_CONFIG,
      });
      await isErrorResponse(response, null, dispatch);
      await response;
      dispatch(push(PAYMENT_METHOD_PATH));
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagGettingPaymentMethod(false));
    }
  };

export {
  flagGettingPaymentMethods,
  showErrorGettingPaymentMethods,
  clearErrorGettingPaymentMethods,
  getPaymentMethods,
  clearPaymentMethods,
  getPaymentMethodsPerSecurityProfile,
  getPaymentMethod,
  putPaymentMethod,
  postPaymentMethod,
  deletePaymentMethod,
  clearPaymentMethod,
};
